import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import PropTypes from 'prop-types'
import loaderIcon from '@assets/images/icons/loader.svg'
import LoaderInline from '@/modules/base/components/loader/loader-inline'
import DEBOUNCE_TIMES from '@/modules/base/components/buttons/debounce-times'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import Direction from '@/enums/direction.enum'

export const ICON_POS = Object.freeze({
  left: 'left',
  right: 'right',
})

export const COLORS = Object.freeze({
  blue: 'blue',
  red: 'red',
  custom: 'custom',
})

export const VARIANTS = Object.freeze({
  fill: 'fill',
  outline: 'outline',
})

const useStyles = makeStyles((theme) => ({
  clickInProgress: {
    cursor: 'default',
    '& $label': {
      background: `${theme.midGrey} !important`,
    },
    '& $icon': {
      background: `${theme.backgroundColorIconDisabled} !important`,
    },
  },
  button: {
    display: 'flex',
    alignItems: 'stretch',
    height: 45,
    padding: 0,
    borderRadius: 5,
    border: 'none',
    outline: 'none',
    overflow: 'hidden',
    fontWeight: 'normal',
    flexShrink: 0,
    background: 'transparent',
    '&:disabled': {
      '& $label': {
        background: theme.midGrey,
      },
      '& $icon': {
        background: theme.backgroundColorIconDisabled,
      },
    },
    '&:disabled:hover': {
      '& $label': {
        background: theme.midGrey,
      },
      '& $icon': {
        background: theme.backgroundColorIconDisabled,
      },
    },
    '&:focus-visible': {
      borderColor: theme.Input.borderColorActive,
    },
  },
  spinnerIcon: ({ iconPos, label }) => {
    const base = {
      justifyContent: 'center',
      height: 45,
      minWidth: 45,
      maxWidth: 45,
      padding: 0,
      fill: theme.buttonColor,
      background: theme.backgroundColorIconDisabled,
      alignItems: 'center',
      '& span': {
        marginTop: 0,
        display: 'flex',
      },
      '& svg': {
        width: '18px !important',
        height: '18px !important',
      },
    }

    if (label) {
      return iconPos === ICON_POS.right ? { ...base, borderTopRightRadius: 5, borderBottomRightRadius: 5 } : { ...base, borderBottomLeftRadius: 5, borderTopLeftRadius: 5 }
    } else {
      return { ...base, borderRadius: 5 }
    }
  },
  icon: ({ iconPos, label, variant }) => {
    const base = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 45,
      minWidth: 45,
      maxWidth: 45,
      padding: 0,
    }

    if (label) {
      return iconPos === ICON_POS.right ? { ...base, borderTopRightRadius: 5, borderBottomRightRadius: 5 } : { ...base, borderBottomLeftRadius: 5, borderTopLeftRadius: 5 }
    } else {
      return { ...base, borderRadius: 5 }
    }
  },
  label: ({ iconPos, icon }) => {
    const base = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0px 25px',
      flexGrow: 1,
      whiteSpace: 'nowrap',
    }

    if (icon) {
      return iconPos === ICON_POS.right ? { ...base, borderBottomLeftRadius: 5, borderTopLeftRadius: 5 } : { ...base, borderTopRightRadius: 5, borderBottomRightRadius: 5 }
    } else {
      return {
        ...base,
        borderRadius: 5,
      }
    }
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  custom: {},
  // eslint-disable-next-line mui-unused-classes/unused-classes
  blue: ({ variant }) => {
    if (variant === VARIANTS.fill) {
      return {
        '& $icon': {
          color: theme.buttonColor,
          background: theme.colorPrimaryButtonBackgroundHover,
          '& svg': {
            fill: theme.buttonColor,
          },
        },
        '&:hover:not(:disabled)': {
          '& $icon': {
            background: theme.colorPrimaryButtonBackground,
            transition: 'all 0.2s ease-out',
          },
          '& $label': {
            background: theme.colorPrimaryButtonBackgroundHover,
            transition: 'all 0.2s ease-out',
          },
        },
        '&:disabled:hover': {
          '& $icon': {
            background: theme.backgroundColorIconDisabled,
          },
          '& $label': {
            background: theme.midGrey,
          },
        },
        '& $label': {
          color: theme.buttonColor,
          background: theme.colorPrimaryButtonBackground,
        },
      }
    } else {
      return {
        border: `1px solid ${theme.borderOutlineColor}`,
        '&:disabled': {
          border: `1px solid ${theme.disabledColor}`,
          '& $label': {
            background: theme.midGrey,
            color: theme.white,
          },
        },
        '& $icon': {
          color: theme.colorPrimaryButtonBackground,
          background: theme.lighterGrey,
          '& svg': {
            fill: theme.colorPrimaryButtonBackground,
          },
        },
        '& $label': {
          color: theme.colorPrimaryButtonBackground,
          background: '#fff',
        },
        '&:hover:not(:disabled)': {
          '& $icon': {
            transition: 'all 0.2s ease-out',
            background: '#fff',
          },
          '& $label': {
            transition: 'all 0.2s ease-out',
            background: theme.lighterGrey,
          },
        },
      }
    }
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  red: ({ variant }) => {
    const base = {
      border: variant === VARIANTS.outline ? `1px solid ${theme.colorDeleteButtonBackground}` : 'none',
      '& $icon': {
        color: theme.buttonColor,
        background: theme.colorDeleteButtonBackgroundHover,
        '& svg': {
          fill: theme.buttonColor,
        },
      },
      '& $label': variant === VARIANTS.fill
        ? {
          color: theme.buttonColor,
          background: theme.colorDeleteButtonBackground,
        }
        : {
          color: theme.colorDeleteButtonBackground,
          background: 'transparent',
        },
    }

    if (variant === VARIANTS.fill) {
      return {
        ...base,
        '&:hover': {
          '& $icon': {
            background: theme.colorDeleteButtonBackground,
            transition: 'all 0.2s ease-out',
          },
          '& $label': {
            background: theme.colorDeleteButtonBackgroundHover,
            transition: 'all 0.2s ease-out',
          },
        },
      }
    } else {
      return {
        ...base,
        '&:hover': {
          '& $label': {
            background: 'rgba(220, 0, 78, 0.04);',
            transition: 'all 0.2s ease-out',
          },
        },
      }
    }
  },
  tooltip: {
    color: theme.black,
  },
  fadeIn: {
    opacity: 1,
    transition: 'opacity 0.4s',
    width: 'auto',
  },
  fadeOut: {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.4s',
    width: 0,
    height: 0,
    minWidth: 0,
    minHeight: 0,
    maxWidth: 0,
    maxHeight: 0,
    padding: 0,
    margin: 0,
    border: 'none',
    overflow: 'hidden',
  },
}))

const ButtonIconSquare = (props, ref) => {
  const {
    id,
    color = COLORS.blue,
    actionId,
    icon,
    label,
    customClass,
    dataQsId,
    onClick,
    iconPos = ICON_POS.left,
    iconFill,
    children,
    className,
    type = 'button',
    disabled = false,
    variant = VARIANTS.fill,
    debounceTime = DEBOUNCE_TIMES.DEFAULT,
    iconWidth = '13px',
    onDebounceStart,
    onDebounceEnd,
    iconClass,
    tooltip = '',
    tooltipWidth = 300,
    tooltipPlacement = Direction.BOTTOM,
    isLoading = false,
    labelClass,
    secret = false,
    ...otherProps
  } = props

  const classes = useStyles({ iconPos, icon, variant, label: label || children })
  const [clickDebounce, setClickDebounce] = useState(false)
  const [isShiftPressed, setIsShiftPressed] = useState(false)
  const iconElm = useMemo(() =>
    icon
      ? (clickDebounce || isLoading
        ? <LoaderInline customClass={classes.spinnerIcon} visible={true} customIcon={loaderIcon} customAnimation='spin 450ms linear infinite'></LoaderInline>
        : <div className={clsx(classes.icon, iconClass)}>
          <SVGIcon
            width={iconWidth}
            height={iconWidth}
            svg={icon}
            fill={iconFill}
          />
        </div>
      )
      : null
  , [icon, clickDebounce, iconWidth, iconFill, classes.icon, iconClass, isLoading])

  const clickDebounceTimeOutRef = useRef()
  const onClickRef = useRef()
  onClickRef.current = onClick

  const onDebounceStartRef = useRef()
  onDebounceStartRef.current = onDebounceStart
  const onDebounceEndRef = useRef()
  onDebounceEndRef.current = onDebounceEnd

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') setIsShiftPressed(true)
    }
    const handleKeyUp = (e) => {
      if (e.key === 'Shift') setIsShiftPressed(false)
    }
    if (secret) {
      window.addEventListener('keydown', handleKeyDown)
      window.addEventListener('keyup', handleKeyUp)
    }
    return () => {
      if (secret) {
        window.removeEventListener('keydown', handleKeyDown)
        window.removeEventListener('keyup', handleKeyUp)
      }
      onDebounceEndRef.current && onDebounceEndRef.current()
      clearTimeout(clickDebounceTimeOutRef.current)
    }
  }, [])

  const onClickHandle = useCallback((e) => {
    if (!clickDebounce) {
      if (debounceTime > 0) {
        setClickDebounce(true)
        onDebounceStartRef.current && onDebounceStartRef.current()
      }
      onClickRef.current && onClickRef.current(e, actionId)
      if (debounceTime > 0) {
        clickDebounceTimeOutRef.current = setTimeout(() => {
          setClickDebounce(false)

          onDebounceEndRef.current && onDebounceEndRef.current()
        }, debounceTime)
      }
    }
  }, [debounceTime])

  let showButton = true
  let fadeClass = ''
  if (secret) {
    showButton = true // always render for transition, but control opacity
    fadeClass = isShiftPressed ? classes.fadeIn : classes.fadeOut
  }

  let buttonContent = (
    <button
      id={id || ''}
      type={type}
      ref={ref}
      className={clsx(clickDebounce ? classes.clickInProgress : '', classes.button, classes[color], customClass, className, fadeClass)}
      disabled={disabled || isLoading || (clickDebounce && onClick != null)}
      onClick={onClickHandle}
      data-qs-id={dataQsId}
      {...otherProps}
    >
      {iconPos === ICON_POS.left && iconElm}
      {(label || children) && (
        <span className={clsx(classes.label, labelClass)}>{label || children}</span>
      )}
      {iconPos === ICON_POS.right && iconElm}
    </button>
  )

  if (tooltip) {
    buttonContent = (<RichTooltip
      content={(<div className={classes.tooltip}>{tooltip}</div>)}
      placement={tooltipPlacement}
      width={tooltipWidth}
    >
      <div>
        {buttonContent}
      </div>
    </RichTooltip>)
  }

  // If not secret, always show. If secret, only show (with fade) if isShiftPressed
  if (!secret || (secret && showButton)) {
    return buttonContent
  } else {
    return null
  }
}

const ForwardedButtonIconSquare = React.forwardRef(ButtonIconSquare)

ForwardedButtonIconSquare.propTypes = {
  id: PropTypes.string,
  color: PropTypes.oneOf([COLORS.blue, COLORS.red, COLORS.custom]),
  actionId: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]),
  label: PropTypes.string,
  className: PropTypes.string,
  customClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  iconClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onClick: PropTypes.func,
  iconPos: PropTypes.oneOf([ICON_POS.right, ICON_POS.left]),
  iconFill: PropTypes.string,
  iconWidth: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf([VARIANTS.fill, VARIANTS.outline]),
  debounceTime: PropTypes.number,
  onDebounceStart: PropTypes.func,
  onDebounceEnd: PropTypes.func,
  children: PropTypes.node,
  tooltip: PropTypes.string,
  tooltipWidth: PropTypes.number,
  tooltipPlacement: PropTypes.oneOf([...Object.values(Direction)]),
  isLoading: PropTypes.bool,
  labelClass: PropTypes.string,
  secret: PropTypes.bool,
  dataQsId: PropTypes.string.isRequired,
}

export default ForwardedButtonIconSquare
